/**
 * components/brief-builder/BriefExportStep.tsx
 *
 * Step 5 — Validation results + YAML / JSON export.
 * Roles: Solution Architect / Admin
 */
import { useState } from 'react'
import type { BriefSession, ExportResponse } from '@/types/brief-builder'

interface Props {
  session: BriefSession
  lastExport: ExportResponse | null
  onExport: () => void
  onBackToEdit?: () => void
  loading?: boolean
}

export default function BriefExportStep({ session, lastExport, onExport, onBackToEdit, loading }: Props) {
  const validation = session.validation_result
  const [format, setFormat] = useState<'yaml' | 'json'>('yaml')

  const activeContent = format === 'yaml' ? lastExport?.yaml_content : lastExport?.json_content

  const handleDownload = () => {
    if (!activeContent) return
    const mime = format === 'yaml' ? 'text/yaml' : 'application/json'
    const ext  = format === 'yaml' ? 'yaml' : 'json'
    const blob = new Blob([activeContent], { type: mime })
    const url  = URL.createObjectURL(blob)
    const a    = document.createElement('a')
    a.href     = url
    a.download = `${session.label.toLowerCase().replace(/\s+/g, '-')}-config.${ext}`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="max-w-3xl mx-auto px-5 py-10 space-y-8">
      <div>
        <h2 className="text-xl font-bold text-slate-900">Validate & Export</h2>
        <p className="text-sm text-slate-500 mt-1">
          Review validation results, then export the final tenant configuration YAML.
        </p>
      </div>

      {/* Validation result */}
      {validation && (
        <section className="space-y-3">
          <div className={`flex items-center gap-3 px-4 py-3 rounded-xl border ${
            validation.valid
              ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
              : 'bg-red-50 border-red-200 text-red-800'
          }`}>
            <span className="text-lg">{validation.valid ? '✅' : '❌'}</span>
            <p className="text-sm font-semibold">
              {validation.valid
                ? 'All business rules pass. Ready to export.'
                : `${validation.errors.length} error(s) must be resolved before export.`}
            </p>
          </div>

          {validation.errors.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-xs font-semibold text-red-600 uppercase tracking-wide">Errors (blocking)</p>
              {validation.errors.map((e, i) => (
                <div key={i} className="flex gap-2 text-xs text-red-700 bg-red-50 rounded-lg px-3 py-2">
                  <span className="shrink-0">•</span>{e}
                </div>
              ))}
            </div>
          )}

          {validation.warnings.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-xs font-semibold text-amber-600 uppercase tracking-wide">Warnings (advisory)</p>
              {validation.warnings.map((w, i) => (
                <div key={i} className="flex gap-2 text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2">
                  <span className="shrink-0">⚠</span>{w}
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {/* Output preview with format toggle */}
      {lastExport && (activeContent) && (
        <section className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-slate-700">Generated Config</p>
            {/* Format toggle */}
            <div className="flex rounded-lg border border-slate-200 overflow-hidden text-xs font-medium">
              {(['yaml', 'json'] as const).map(f => (
                <button
                  key={f}
                  onClick={() => setFormat(f)}
                  className={`px-3 py-1.5 uppercase tracking-wide transition-colors
                    ${format === f
                      ? 'bg-indigo-600 text-white'
                      : 'bg-white text-slate-500 hover:bg-slate-50'}`}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>
          <pre className="bg-slate-900 text-emerald-300 text-xs rounded-xl p-4 overflow-x-auto
                          max-h-96 whitespace-pre font-mono leading-relaxed">
            {activeContent}
          </pre>
        </section>
      )}

      {/* Actions */}
      <div className="flex gap-3 justify-between items-center">
        {/* Back to edit */}
        {onBackToEdit && (
          <button
            onClick={onBackToEdit}
            className="px-4 py-2 rounded-lg border border-slate-200 text-slate-600 text-sm
                       font-medium hover:bg-slate-50 transition-colors flex items-center gap-1.5"
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
              <path d="M7.5 2L3.5 6l4 4" stroke="currentColor"
                    strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Back to Edit
          </button>
        )}
        <div className="flex gap-3 ml-auto">
          {!lastExport && (
            <button
              onClick={onExport}
              disabled={loading || !validation?.valid}
              className="px-6 py-2.5 rounded-lg bg-indigo-600 text-white text-sm font-semibold
                         hover:bg-indigo-700 disabled:opacity-40 transition-colors"
            >
              {loading ? 'Generating…' : 'Generate Config'}
            </button>
          )}
          {lastExport && activeContent && (
            <button
              onClick={handleDownload}
              className="px-6 py-2.5 rounded-lg bg-emerald-600 text-white text-sm font-semibold
                         hover:bg-emerald-700 transition-colors flex items-center gap-2"
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
                <path d="M7 2v7M4 6l3 3 3-3M2 11h10" stroke="currentColor"
                      strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Download {format.toUpperCase()}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
